import { TASK_STATUS } from '../../constants/Tasks';
import { Task, BatchData } from '../../constants/taskInterfaces';
import { StepStatus } from '../../types/stepStatus';

export interface TaskAttrs {
  uuid: string;
  note: string;
  test: boolean;
  waypointA: string;
  waypointB: string;
  requestedBy: string | null;
  isScheduled: boolean;
  batchData?: TaskAttrsBatchData;
  failedToPullTask: boolean;
  restricted: boolean;
  codeRequired?: boolean;
  codeGivenTo?: string;
}

export interface TaskAttrsBatchData {
  type: string;
  id: string;
  index: number;
  total: number;
  pickups: string[];
  dropoffs: string[];
}

export const BatchTypes = {
  // TODO Turn into enum when migrate enough files to TypeScript
  oneToMany: '1_M',
  manyToOne: 'M_1',
};

/**
 * Get requested by value
 * @param {Task} task
 * @returns {string | null}
 */
export const getRequestedBy = (task: Task): string | null => {
  if (!task.task_data.batch && task.requested_by !== task.task_level_args.start_goal.value) {
    return task.requested_by;
  }
  return null;
};

/**
 * Extract task data required by the load and unload screens from the local task cache.
 * @param {string} taskId - Task UUID
 * @param {Task[]} tasks - The array of cached tasks
 * @returns {TaskAttrs}
 */
export const extractTaskAttrs = (taskId: string, tasks: Task[]): TaskAttrs => {
  const cachedTask = tasks.find((t) => t.uuid == taskId);
  if (!cachedTask) {
    throw new Error(`In-progress task ${taskId} could not be found in cached tasks`);
  }
  let taskAttrs: TaskAttrs = {
    uuid: taskId,
    note: cachedTask.task_data.sender_note || '',
    test: cachedTask.task_data.test || false,
    requestedBy: getRequestedBy(cachedTask),
    isScheduled: !!cachedTask.task_data.scheduled,
    waypointA: cachedTask.task_level_args.start_goal.value,
    waypointB: cachedTask.task_level_args.end_goal.value,
    failedToPullTask: false,
    restricted: cachedTask.task_data.restricted || false,
    codeRequired: cachedTask.task_data.code_required || false,
    codeGivenTo: cachedTask.task_data.code_given_to || '',
  };
  const cachedBatchData = cachedTask.task_data.batch;
  if (cachedBatchData) {
    const batchTasks = findBatchTasks(cachedBatchData.id, tasks);
    validateBatchComplete(batchTasks);
    taskAttrs.batchData = extractBatchData(cachedBatchData, batchTasks);
  }
  return taskAttrs;
};

/**
 * Get info about a batch task that is required by the load and unload screens.
 * @param {BatchData} cachedBatchData - Batch data as cached in task cache
 * @param {Task[]} batchTasks - The array of batch tasks
 * @returns {TaskAttrsBatchData}
 */
export const extractBatchData = (cachedBatchData: BatchData, batchTasks: Task[]): TaskAttrsBatchData => {
  if (batchTasks.some((t) => !t.task_data.batch || t.task_data.batch.id !== cachedBatchData.id)) {
    throw new Error(`All batch tasks in batchTasks array must have same batch ID ${cachedBatchData.id}`);
  }
  let type: string;
  let pickups: string[];
  let dropoffs: string[];
  switch (cachedBatchData.batchType) {
    case 'oneToMany':
      type = BatchTypes.oneToMany;
      pickups = [batchTasks[0].task_level_args.start_goal.value];
      dropoffs = batchTasks.map((t) => t.task_level_args.end_goal.value);
      break;
    case 'manyToOne':
      type = BatchTypes.manyToOne;
      pickups = batchTasks.map((t) => t.task_level_args.start_goal.value);
      dropoffs = [batchTasks[batchTasks.length - 1].task_level_args.end_goal.value];
      break;
    default:
      throw new Error(
        `task_data.batch.batchType value ${cachedBatchData.batchType} was not recognized, expected 'oneToMany' or 'manyToOne'`,
      );
  }
  return {
    id: cachedBatchData.id,
    index: cachedBatchData.index,
    total: batchTasks.length,
    type: type,
    pickups: pickups,
    dropoffs: dropoffs,
  };
};

/**
 * Get array subset of batch tasks matching a certain batch ID.
 * Batch tasks will be sorted by index if index exists.
 * @param {string} batchId - Batch ID
 * @param {Task[]} tasks - The array of cached tasks
 * @returns {Task[]}
 */
export const findBatchTasks = (batchId: string, tasks: Task[]): Task[] => {
  const batchTasks = tasks.filter((t) => !!t.task_data.batch && t.task_data.batch.id === batchId);
  return batchTasks.sort((a: Task, b: Task) => {
    if (!a.task_data.batch || !b.task_data.batch) {
      return 0;
    } else {
      return a.task_data.batch.index - b.task_data.batch.index;
    }
  });
};

export const validateBatchComplete = (batchTasks: Task[]) => {
  let batchIds = new Set();
  let batchIndices = new Set();
  let batchTotals = new Set();
  batchTasks.forEach((t) => {
    if (!t.task_data.batch) {
      throw new Error(`Task ${t.uuid} does not contain the batch field, cannot be part of a batch task`);
    }
    batchIds.add(t.task_data.batch?.id);
    batchIndices.add(t.task_data.batch?.index);
    if (t.status !== TASK_STATUS.IN_PROGRESS) {
      batchTotals.add(t.task_data.batch?.total);
    }
  });
  if (batchIds.size > 1) {
    throw new Error(`Batch tasks do not share the same ID, contains batch IDs ${Array.from(batchIds)}`);
  }
  if (batchTotals.size > 1) {
    throw new Error(`Batch tasks contain inconsistent batch totals, contains ${Array.from(batchTotals)}`);
  }
  const batchTotal = batchTotals.values().next().value;
  for (let i = 0; i < batchTotal - 1; i++) {
    if (!batchIndices.has(i)) {
      throw new Error(
        `Batch task set incomplete, batch task ${i + 1}/${batchTotal} was not found (and more may be missing)`,
      );
    }
  }
};

export const extractFallbackTaskAttrs = (stepStatus: StepStatus): TaskAttrs => {
  return {
    uuid: stepStatus.task_uuid,
    note: '',
    test: false,
    waypointA: stepStatus.task_args.start_goal.value,
    waypointB: stepStatus.task_args.end_goal.value,
    requestedBy: null,
    isScheduled: false,
    failedToPullTask: true,
    restricted: false,
    codeRequired: false,
    codeGivenTo: '',
  };
};
